﻿import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { PERMISSIONS } from '@pms/shared';
import type { ApiError } from '@pms/shared';
import { requirementsService, type RequirementListItem } from '@/services/requirements.service';
import { PermissionGate } from '@/components/guards/PermissionGate';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SearchInput } from '@/components/ui/search-input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { RequirementStatusDropdown } from './components/RequirementStatusDropdown';
import { CreateRequirementModal } from './components/CreateRequirementModal';
import { EditRequirementModal } from './components/EditRequirementModal';
import { RequirementDetailDrawer } from './components/RequirementDetailDrawer';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

function formatDate(str: string | null | undefined): string {
  if (!str) return '—';
  return new Date(str).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatCurrency(v: number | null | undefined): string {
  if (v == null) return '—';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v);
}

const PRIORITY_BADGE: Record<string, string> = {
  critical: 'bg-rose-100 text-rose-700 border-rose-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  medium: 'bg-teal-100 text-teal-700 border-teal-200',
  low: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

export function RequirementsListPage() {
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const limit = 15;

  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [statusId, setStatusId] = useState('');
  const [priority, setPriority] = useState('');

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setDebouncedQ(q); setPage(1); }, 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q]);

  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState<RequirementListItem | null>(null);
  const [viewRow, setViewRow] = useState<RequirementListItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);

  const params = {
    page,
    limit,
    q: debouncedQ || undefined,
    category_id: categoryId || undefined,
    status_id: statusId || undefined,
    priority: (priority || undefined) as 'low' | 'medium' | 'high' | 'critical' | undefined,
  };

  const { data: listData, isLoading, isError } = useQuery({
    queryKey: ['requirements', 'list', params],
    queryFn: () => requirementsService.listRequirements(params),
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['requirements', 'categories'],
    queryFn: () => requirementsService.listCategories(),
  });

  const { data: statuses = [] } = useQuery({
    queryKey: ['requirements', 'statuses'],
    queryFn: () => requirementsService.listStatuses(),
  });

  const updateFilter = (setter: (v: string) => void) => (v: string) => {
    setter(v === '__all__' ? '' : v);
    setPage(1);
  };

  const deleteMutation = useMutation({
    mutationFn: (id: string) => requirementsService.deleteRequirement(id),
    onSuccess: () => {
      toast.success('Requirement deleted');
      void queryClient.invalidateQueries({ queryKey: ['requirements', 'list'] });
      void queryClient.invalidateQueries({ queryKey: ['requirements', 'stats'] });
      setDeleteTarget(null);
    },
    onError: (e) => { toast.error(errMsg(e)); setDeleteTarget(null); },
  });

  const columns: Column<RequirementListItem>[] = [
    {
      key: 'title',
      header: 'Title',
      cell: (row) => (
        <div className="max-w-xs">
          <p className="text-sm font-medium truncate">{row.title}</p>
          {row.category && (
            <p className="text-xs text-muted-foreground">{row.category.name}</p>
          )}
        </div>
      ),
    },
    {
      key: 'priority',
      header: 'Priority',
      cell: (row) => (
        <Badge variant="outline" className={`capitalize text-xs ${PRIORITY_BADGE[row.priority] ?? ''}`}>
          {row.priority}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row) => <RequirementStatusDropdown requirement={row} />,
    },
    {
      key: 'requested_by',
      header: 'Requested By',
      cell: (row) => (
        <span className="text-sm">{row.requested_by?.name ?? '—'}</span>
      ),
    },
    {
      key: 'estimated_cost',
      header: 'Est. Cost',
      cell: (row) => (
        <span className="text-sm">{formatCurrency(row.estimated_cost)}</span>
      ),
    },
    {
      key: 'due_date',
      header: 'Due Date',
      cell: (row) => (
        <span className="text-sm text-muted-foreground">{formatDate(row.due_date)}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-24',
      cell: (row) => (
        <div className="flex items-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            onClick={() => setViewRow(row)}
            title="View details"
          >
            <Eye className="h-4 w-4" />
          </Button>
          <PermissionGate permission={PERMISSIONS.REQUIREMENTS_EDIT}>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={() => setEditRow(row)}
              title="Edit requirement"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          </PermissionGate>
          <PermissionGate permission={PERMISSIONS.REQUIREMENTS_DELETE}>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-muted-foreground hover:text-destructive"
              onClick={() => setDeleteTarget({ id: row.id, title: row.title })}
              title="Delete requirement"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </PermissionGate>
        </div>
      ),
    },
  ];

  const pagination = listData
    ? {
        page: listData.pagination.page,
        totalPages: listData.pagination.totalPages,
        total: listData.pagination.total,
        onPageChange: (p: number) => setPage(p),
      }
    : undefined;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Requirements</h1>
          <p className="text-sm text-muted-foreground">Track procurement and resource requirements</p>
        </div>
        <PermissionGate permission={PERMISSIONS.REQUIREMENTS_CREATE}>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" /> New Requirement
          </Button>
        </PermissionGate>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={q}
          onChange={setQ}
          placeholder="Search requirements…"
          containerClassName="min-w-[200px] flex-1 max-w-xs"
        />

        <Select value={categoryId || '__all__'} onValueChange={updateFilter(setCategoryId)}>
          <SelectTrigger className="h-9 w-44">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Categories</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={statusId || '__all__'} onValueChange={updateFilter(setStatusId)}>
          <SelectTrigger className="h-9 w-40">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Statuses</SelectItem>
            {statuses.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                <span className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                  {s.name}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={priority || '__all__'} onValueChange={updateFilter(setPriority)}>
          <SelectTrigger className="h-9 w-36">
            <SelectValue placeholder="All Priorities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Priorities</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>

        {listData && (
          <span className="ml-auto text-xs text-muted-foreground">
            {listData.pagination.total}{' '}
            {listData.pagination.total === 1 ? 'requirement' : 'requirements'}
          </span>
        )}
      </div>

      <DataTable
        columns={columns}
        rows={listData?.data ?? []}
        rowKey={(row) => row.id}
        loading={isLoading}
        error={isError}
        onRetry={() => void queryClient.invalidateQueries({ queryKey: ['requirements', 'list'] })}
        empty="No requirements match the current filters"
        pagination={pagination}
      />

      <CreateRequirementModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <EditRequirementModal open={!!editRow} requirement={editRow} onClose={() => setEditRow(null)} />
      <RequirementDetailDrawer open={!!viewRow} requirement={viewRow} onClose={() => setViewRow(null)} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Requirement</AlertDialogTitle>
            <AlertDialogDescription>
              Delete &quot;{deleteTarget?.title}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
